(function () {
  'use strict';

  // ============================================================
  // BRAND CONFIG — the values most likely to change per brand.
  // See README.md in this folder for the full checklist.
  // ============================================================
  const BRAND = {
    fontHeading: '"Roboto Condensed", sans-serif',
    fontWeightHeading: '700',
    colorText: '#1C1C1C',
    colorHover: '#044879',
    colorBorder: '#d8d5d1',
    accent: '#044879', // active-tab indicator color
    tabStyle: 'underline', // 'underline' (Jockey-style border) or 'pill' (rounded background chip)
    radius: '0px', // used only when tabStyle is 'pill'
    tabTextTransform: 'uppercase',
    tabLetterSpacing: '2px',
  };

  const SEL = {
    // RA's live theme uses a custom <x-modal> web component whose id is
    // dynamic per-product/section but always prefixed "size-chart-", and the
    // trigger button carries a matching aria-controls attribute — neither
    // matches Jockey's static .js-size-chart-* classes. Verified live 2026-09-18.
    openBtn:   '[aria-controls^="size-chart"]',
    closeBtn:  '[aria-label="Close"]', // lives inside the modal's shadow DOM — see closeModal() note below
    container: 'x-modal[id^="size-chart"]',
    tableWrap: '.table-scroller',
    table:     '.size-table',
  };

  // RA's live Size Guide modal has 7 fixed category tables, each preceded by
  // its own .heading-section div (not an <h4> inside the table, like
  // Jockey's theme) — verified live 2026-09-18 against a real PDP:
  //   Unisex Classic American Basics / Mens Jackets T-Shirts & Outerwear /
  //   Mens Casual Pants & Shorts / Women's Jackets T-Shirts & Outerwear /
  //   Women's Casual Pants & Shorts / Kids Unisex Size Chart / Footwear Size Guide
  // `match` assigns each table its slug from that heading text.
  const CATEGORY_RULES = [
    { match: /mens jackets|mens.*outerwear|mens.*t-shirts/i,             slug: 'mens-tops' },
    { match: /mens casual pants|mens.*shorts/i,                          slug: 'mens-bottoms' },
    { match: /women'?s jackets|women'?s.*outerwear|women'?s.*t-shirts/i, slug: 'womens-tops' },
    { match: /women'?s casual pants|women'?s.*shorts/i,                  slug: 'womens-bottoms' },
    { match: /kids/i,                                                    slug: 'kids' },
    { match: /footwear/i,                                                slug: 'footwear' },
    { match: /.*/,                                                       slug: 'general' }, // Unisex Classic American Basics + catch-all
  ];

  // Short, human labels for the tab buttons — RA's real heading text ("MENS
  // JACKETS T-SHIRTS & OUTERWEAR") is too long to use as-is, unlike Jockey's
  // shorter table titles that just needed a prefix/suffix stripped.
  const TAB_LABELS = {
    'mens-tops': "Men's Tops",
    'mens-bottoms': "Men's Bottoms",
    'womens-tops': "Women's Tops",
    'womens-bottoms': "Women's Bottoms",
    kids: 'Kids',
    footwear: 'Footwear',
    general: 'Unisex Basics',
  };

  let built = false;

  function log(...args) { console.log('[size-guide-tabs]', ...args); }

  function guessDefaultSlug() {
    const haystack = [
      document.querySelector('h1')?.textContent || '',
      document.title || '',
      location.pathname || '',
    ].join(' ');

    if (/\bkids?\b|\bjunior\b|\byouth\b/i.test(haystack)) return 'kids';
    if (/shoe|sneaker|footwear/i.test(haystack)) return 'footwear';

    const gender = /\bwomen'?s\b/i.test(haystack) ? 'womens' : 'mens';
    const garment = /pant|short|jogger|track/i.test(haystack) ? 'bottoms' : 'tops';
    return `${gender}-${garment}`;
  }

  function slugFor(titleText) {
    const rule = CATEGORY_RULES.find((r) => r.match.test(titleText));
    return rule ? rule.slug : 'general';
  }

  function buildTabs(container) {
    if (built) return;

    const wraps = Array.from(container.querySelectorAll(SEL.tableWrap));
    if (!wraps.length) return; // tables not rendered yet, try again later

    const entries = wraps.map((wrap) => {
      const table = wrap.querySelector(SEL.table);
      // RA's title is a standalone .heading-section div immediately before
      // the table wrap; fall back to Jockey's in-table <h4> just in case.
      const headingDiv =
        wrap.previousElementSibling && wrap.previousElementSibling.classList.contains('heading-section')
          ? wrap.previousElementSibling
          : null;
      const titleEl = headingDiv || table?.querySelector('th h4, td h4, h4');
      const title = (titleEl?.textContent || 'Size Guide').trim();
      const slug = slugFor(title);
      // Drop the redundant title now that it lives in the tab label.
      if (headingDiv) headingDiv.remove();
      else titleEl?.closest('tr')?.remove();
      return { wrap, title, slug };
    });

    const nav = document.createElement('div');
    nav.className = 'sgt-tabs';
    nav.setAttribute('role', 'tablist');
    nav.innerHTML = entries
      .map(
        (e) =>
          `<button type="button" class="sgt-tab" role="tab" data-slug="${e.slug}" aria-selected="false">${shortLabel(e.title, e.slug)}</button>`
      )
      .join('');

    const firstWrap = entries[0].wrap;
    firstWrap.parentElement.insertBefore(nav, firstWrap);

    entries.forEach((e) => e.wrap.classList.add('sgt-panel'));

    nav.addEventListener('click', (ev) => {
      const btn = ev.target.closest('.sgt-tab');
      if (!btn) return;
      activate(btn.dataset.slug);
    });

    injectStyles();
    built = true;
    container.__sgtEntries = entries;
    container.__sgtNav = nav;
    log('tabs built:', entries.map((e) => e.slug));
  }

  function shortLabel(title, slug) {
    if (TAB_LABELS[slug]) return TAB_LABELS[slug];
    return title
      .replace(/^Men'?s\s*/i, '')
      .replace(/Size Guide$/i, '')
      .trim() || 'General';
  }

  function activate(slug) {
    const container = document.querySelector(SEL.container);
    if (!container || !container.__sgtEntries) return;
    const entries = container.__sgtEntries;
    const match = entries.find((e) => e.slug === slug) || entries.find((e) => e.slug === 'general') || entries[0];

    entries.forEach((e) => {
      const isActive = e === match;
      e.wrap.style.display = isActive ? '' : 'none';
    });
    container.__sgtNav.querySelectorAll('.sgt-tab').forEach((btn) => {
      btn.setAttribute('aria-selected', btn.dataset.slug === match.slug ? 'true' : 'false');
    });
  }

  function openModal() {
    const container = document.querySelector(SEL.container);
    if (!container) return;
    buildTabs(container);
    // RA's <x-modal> already opens itself correctly (unlike Jockey's broken
    // trigger, which this class toggle was originally written to fix) — kept
    // as a harmless no-op in case a future theme update keys visibility off it.
    document.body.classList.add('size-chart-is-open');
    activate(guessDefaultSlug());
    log('opened, default tab:', guessDefaultSlug());
  }

  // The real close button lives inside <x-modal>'s shadow DOM, so an
  // event's retargeted `e.target` outside that boundary can never match
  // SEL.closeBtn — this never fires for RA. Left in place since it's
  // harmless (undoes the no-op class above) and costs nothing to keep.
  function closeModal() {
    document.body.classList.remove('size-chart-is-open');
  }

  function injectStyles() {
    // Two visual treatments for the active tab, selected by BRAND.tabStyle:
    // 'underline' (thin border under the label, Jockey/Russell style) or
    // 'pill' (rounded background chip, Mitch Dowd's signature).
    const activeTabCss =
      BRAND.tabStyle === 'pill'
        ? `
      .sgt-tab { border-radius: ${BRAND.radius}; padding: 6px 16px; border-bottom: none; }
      .sgt-tab[aria-selected="true"] { color: #fff; background: ${BRAND.accent}; }
    `
        : `
      .sgt-tab { border-bottom: 2px solid transparent; padding: 0 0 10px; }
      .sgt-tab[aria-selected="true"] { color: ${BRAND.colorText}; border-bottom-color: ${BRAND.accent}; }
    `;

    const style = document.createElement('style');
    style.textContent = `
      .sgt-tabs {
        display: flex;
        gap: ${BRAND.tabStyle === 'pill' ? '10px' : '20px'};
        margin: 4px 0 20px;
        ${BRAND.tabStyle === 'pill' ? '' : `border-bottom: 1px solid ${BRAND.colorBorder};`}
        overflow-x: auto;
      }
      .sgt-tab {
        appearance: none;
        background: none;
        border: none;
        font: inherit;
        font-family: ${BRAND.fontHeading};
        font-weight: ${BRAND.fontWeightHeading};
        font-size: 13px;
        letter-spacing: ${BRAND.tabLetterSpacing};
        text-transform: ${BRAND.tabTextTransform};
        color: ${BRAND.colorText};
        cursor: pointer;
        white-space: nowrap;
      }
      .sgt-tab:hover { color: ${BRAND.colorHover}; }
      ${activeTabCss}
      .sgt-panel table.size-table { margin-top: 0; }
    `;
    document.head.appendChild(style);
  }

  // Event delegation: works regardless of when the modal markup renders,
  // and regardless of whether the theme's own (currently broken) handler fires.
  document.addEventListener('click', (e) => {
    if (e.target.closest(SEL.openBtn)) openModal();
    if (e.target.closest(SEL.closeBtn)) closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeModal();
  });

  log('loaded');
})();
